import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Card } from '../../../../components/Card';
import { Notify } from '../../../../components/Notify';
import { useAuth } from '../../../../hooks/useAuth';
import { useCart } from '../../../../hooks/useCart';
import { numberWithCommas } from '../../../../utils/numberWithCommas';
import { IAwards } from '../../interfaces';
import { ModalDetailsAward } from '../ModalDetailsAward';
import styles from './RescueCard.module.css';
import { usePoints } from '../../../../hooks/usePoints';

interface IRescueCard {
  award: IAwards;
}

export function RescueCard({ award }: IRescueCard) {
  const { client } = useAuth();
  const { remainingPoints, availableToRedeem } = usePoints();
  const { cart, addProductToCart } = useCart();
  const [reasonDisabled, setReasonDisabled] = useState('');
  const [modalDetailsAwardIsOpen, setModalDetailsAwardIsOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (
      award.valueType &&
      award.valueType.value === 'variable' &&
      award.paymentValueMinimun
    ) {
      if (
        availableToRedeem &&
        availableToRedeem < Number(award.paymentValueMinimun)
      ) {
        setReasonDisabled('Saldo insuficiente');
      } else {
        setReasonDisabled('');
      }
    }
  }, [availableToRedeem]);

  useEffect(() => {
    if (remainingPoints === 0 || award.premiumPrice > Number(remainingPoints)) {
      setReasonDisabled('Saldo insuficiente');
    } else {
      if (
        !award.inStock ||
        award.inStock <= 0 ||
        cart.find(
          (item) => item.id === award.premiumId && item.amount > award.inStock
        )
      ) {
        setReasonDisabled('Sem estoque');
      } else if (!award.active) {
        setReasonDisabled('Inativo');
      } else {
        setReasonDisabled('');
      }
    }
  }, [remainingPoints]);

  function handleCart() {
    if (
      award.valueType &&
      award.valueType.value === 'variable' &&
      award.paymentValueMinimun
    ) {
      if (availableToRedeem >= Number(award.paymentValueMinimun)) {
        handleAddAwardVariable();
      } else {
        // inserir razão
        return Notify(
          'Você ainda não tem a pontuação mínima exigida para resgatar esse prêmio',
          'warn'
        );
      }
    } else if (award.valueType && award.valueType.value === 'variable') {
      handleAddAwardVariable();
    } else {
      handleAddAward();
    }
  }

  const verifyDisableButtonAdd = (award: IAwards) => {
    if (remainingPoints === 0) {
      return true;
    }

    if (award.premiumPrice > Number(remainingPoints)) {
      return true;
    }

    if (!award.inStock || !award.active || award.inStock <= 0) {
      return true;
    }

    return false;
  };

  const handleAddAwardVariable = () => {
    if (client.clientUrl === 'brconsorcio') {
      if (availableToRedeem < 30) {
        return Notify(
          'Você ainda não tem a pontuação mínima exigida para resgatar esse prêmio',
          'warn'
        );
      }
    }
    if (availableToRedeem < 100) {
      return Notify(
        'Você ainda não tem a pontuação mínima exigida para resgatar esse prêmio',
        'warn'
      );
    }
    if (cart.length === 0) {
      if (Number(remainingPoints) >= Number(availableToRedeem)) {
        return addProductToCart(award);
      }
    }

    if (cart.length > 0) {
      const findItemVariable = cart.find(
        (item) => item.valueType && item.valueType.value === 'variable'
      );

      if (findItemVariable) {
        return Notify('Adicione apenas um prêmio desse tipo', 'warn');
      }
      const findItemFixed = cart.find(
        (item) => item.valueType && item.valueType.value === 'fixed'
      );
      if (findItemFixed) {
        return Notify('Já existe um prêmio diferente em seu carrinho', 'warn');
      }
      if (cart.length === 1) {
        return Notify('Já existe um prêmio diferente em seu carrinho', 'warn');
      }
    }
  };

  const handleAddAward = () => {
    if (cart.length === 0) {
      if (Number(remainingPoints) >= Number(award.premiumPrice)) {
        return addProductToCart(award);
      }
    }

    const findItemVariable = cart.find(
      (item) => item.valueType && item.valueType.value === 'variable'
    );

    if (findItemVariable) {
      return Notify('Adicione apenas prêmios de mesmo tipo', 'warn');
    }

    const findItemEqual = cart.find(
      (item) => item.premiumType.value === award.premiumType.value
    );

    if (findItemEqual) {
      addProductToCart(award);
    } else {
      Notify('Adicione apenas prêmios de mesmo tipo', 'warn');
    }
  };

  let amount = 0;
  if (cart) {
    const selected = cart.find((item) => item.premiumId === award.premiumId);
    amount = (selected && selected.amount) || 0;
  }

  const handleModalDetailsAward = () => {
    setModalDetailsAwardIsOpen(!modalDetailsAwardIsOpen);
  };

  const caracterLimiter = (str: string) => {
    return str && str.length > 50 ? `${str.slice(0, 49)}...` : str;
  };

  return (
    <div>
      <div className="mb-3">
        <Card>
          <div className="d-flex">
            <div className="d-block">
              <div className={`${styles.image} me-3`}>
                <img src={award.premiumImage} alt="prêmio" />
              </div>
            </div>
            <div className="d-block">
              <p className="m-0 mb-2 fs-5 fw-semibold">{award.premiumName}</p>
              <p className="m-0 tiny-text">
                {caracterLimiter(award.premiumDescription)}
              </p>
              <span
                className={`fw-semibold ${styles.pointer}`}
                onClick={handleModalDetailsAward}
              >
                Ver mais
              </span>
            </div>
          </div>
          <div
            className="d-flex mt-3
          "
          >
            <div className="d-flex container-fluid align-items-center p-0">
              {!award.valueType || award.valueType.value === 'fixed' ? (
                <>
                  {award.premiumPrice === 1 ? (
                    <span className="tiny-text">{`${numberWithCommas(
                      award.premiumPrice
                    )} ${client?.coinName ? client.coinName.replace(/\(\w*\)/g, '') : 'ponto'}`}</span>
                  ) : (
                    <span className="tiny-text">{`${numberWithCommas(
                      award.premiumPrice
                    )} ${client?.coinName ? client.coinName.replace(/\((\w+)\)/, (_, conteudo) => conteudo) : 'pontos'}`}</span>
                  )}
                </>
              ) : (
                <span>{`${numberWithCommas(availableToRedeem)} ${client?.coinName ? client.coinName.replace(/\((\w+)\)/, (_, conteudo) => conteudo) : 'pontos'}`}</span>
              )}
            </div>
            <div className="d-flex flex-row-reverse container-fluid p-0">
              {reasonDisabled ? (
                <span className="badge text-bg-danger tiny-text">
                  {reasonDisabled}
                </span>
              ) : (
                <Button
                  style={{ padding: '2px 10px' }}
                  icon="shopping_cart"
                  text={reasonDisabled ? reasonDisabled : `${amount} Adicionar`}
                  color={reasonDisabled ? 'danger' : 'primary'}
                  isLoading={false}
                  onClick={handleCart}
                  disabled={
                    reasonDisabled ? true : verifyDisableButtonAdd(award)
                  }
                />
              )}
            </div>
          </div>
        </Card>
      </div>
      <ModalDetailsAward
        award={award}
        modalIsOpen={modalDetailsAwardIsOpen}
        toggleModalIsOpen={handleModalDetailsAward}
      />
    </div>
  );
}
